/**
 * @file 知识库 NewRegionSelect 选区构建 / 反解（Layer 2b）
 *
 * 面板选区约定：
 * - 回填：整库写 regions，文档级写 markets
 * - 交互：整库写 regions；部分文档写 markets；传给 NewRegionSelect 前 expand，onChange 后 collapse
 * - 提交：resolveCommitKnowledgeTags + collapseSelectedKnowledgeToWholeKb → @库名
 */
import { uniq } from 'lodash-es';
import React from 'react';
import { VALUE_TYPE_MARKET, VALUE_TYPE_REGION } from '@home/components/NewRegionSelect/config';
import type {
  OptionsItemType,
  RegionSelectValueType,
  ChildrenItemType,
} from '@home/components/NewRegionSelect/type';
import type { IKnowledgeBaseItem, IKnowledgeDocItem } from '../hooks/useKnowledgeBaseApi';
import {
  getKnowledgeTagKey,
  type ISelectedKnowledgeTagData,
} from '../components/SelectedKnowledgeTag';
import { getChannelIcon } from './channelIconMap';
import { KbRichItem } from '../components/KbRichItem';
import { resolveTenantId, collapseSelectedKnowledgeToWholeKb, isWholeKbTag } from './knowledgeBaseUtils';

/** 空选区默认值 */
export const KB_EMPTY_VALUE: RegionSelectValueType = {
  type: '',
  regions: [],
  markets: [],
};

/**
 * 构建知识库 NewRegionSelect 选项列表（左栏 + 右栏结构）
 * 左栏永远不读 kb.docs；右栏对已缓存 docsByKbId 的库注入文档子项（半选回填需多库文档）。
 * @param kbList - 知识库列表（列表 API 原始数据，不含按需文档）
 * @param filterKeyword - 搜索关键词（可选）
 * @param docsByKbId - 已按需加载的文档缓存
 * @param highlightKeyword - 左栏高亮关键词（button 搜索框）
 * @returns NewRegionSelect 可用的选项列表
 */
export const buildKnowledgeOptions = (
  kbList: IKnowledgeBaseItem[],
  filterKeyword = '',
  docsByKbId: Record<string, IKnowledgeDocItem[]> = {},
  highlightKeyword = '',
): OptionsItemType[] => {
  const kw = filterKeyword.trim().toLowerCase();
  const highlightKw = highlightKeyword.trim();
  return kbList
    .filter((kb) => !kw || kb.kbName.toLowerCase().includes(kw))
    .map((kb) => {
      const explicitDocs = docsByKbId[kb.kbId] || [];
      const mappedDocs: ChildrenItemType[] = explicitDocs
        .filter((doc) => !kw || doc.docName.toLowerCase().includes(kw))
        .map((doc) => ({
          value: `${kb.kbId}__${doc.docId}`,
          text: doc.docName,
          textCN: doc.docName,
          parent: kb.kbId,
          icon: getChannelIcon(doc.channel, doc.fileType),
        }));

      /** 未加载文档时用 __empty 占位（含 loading 中），左栏整库可立即勾选 */
      let docs: ChildrenItemType[] = mappedDocs;
      const hasDocCount = (kb.docCount ?? 0) > 0;
      if (!mappedDocs.length && hasDocCount) {
        docs = [{
          value: `${kb.kbId}__empty`,
          text: ' ',
          textCN: ' ',
          parent: kb.kbId,
        }];
      }

      return {
        value: kb.kbId,
        text: kb.kbName,
        textCN: kb.kbName,
        hideChildrenCount: true,
        render: (
          <KbRichItem
            name={kb.kbName}
            docCount={kb.docCount || 0}
            updatedAt={kb.updatedAt}
            highlightKeyword={highlightKw}
          />
        ),
        children: docs,
      };
    }) as OptionsItemType[];
};

/** 面板回填涉及的所有 kbId（含整库 @库名） */
export const collectKbIdsForPanelRestore = (
  selectedItems: ISelectedKnowledgeTagData[],
): string[] => uniq(selectedItems.map((item) => String(item.kbId)));

/**
 * 从已提交 tag 还原面板选区：整库写 regions，文档级写 markets（仅 docName 需等文档加载）。
 */
export const derivePanelSelectValue = (
  selectedItems: ISelectedKnowledgeTagData[],
  kbList: IKnowledgeBaseItem[],
  docsByKbId: Record<string, IKnowledgeDocItem[]>,
): RegionSelectValueType => {
  if (!selectedItems.length) {
    return KB_EMPTY_VALUE;
  }

  const kbMap = new Map(kbList.map((kb) => [String(kb.kbId), kb]));
  const regionIds: string[] = [];
  const marketIds: string[] = [];

  selectedItems.forEach((item) => {
    const kbId = String(item.kbId);
    if (isWholeKbTag(item)) {
      regionIds.push(kbId);
      return;
    }

    if (item.docId) {
      marketIds.push(`${kbId}__${item.docId}`);
      return;
    }

    if (item.docName) {
      const docs = docsByKbId[kbId] ?? kbMap.get(kbId)?.docs ?? [];
      const doc = docs.find((d) => d.docName === item.docName);
      if (doc) {
        marketIds.push(`${kbId}__${doc.docId}`);
      }
    }
  });

  if (!regionIds.length && !marketIds.length) {
    return KB_EMPTY_VALUE;
  }

  const regions = uniq(regionIds).filter((kbId) => {
    return !marketIds.some((market) => market.startsWith(`${kbId}__`));
  });

  return {
    type: VALUE_TYPE_REGION,
    regions,
    markets: uniq(marketIds).filter((market) => {
      const kbId = market.split('__')[0];
      return !regions.includes(kbId);
    }),
  };
};

/**
 * 面板再次打开时左列默认高亮库：优先已选区第一个库，无选区再用列表第一项
 */
export const resolveDefaultPanelKbId = (
  selectedItems: ISelectedKnowledgeTagData[],
  kbList: IKnowledgeBaseItem[],
): string => {
  if (selectedItems.length > 0) {
    const firstKbId = String(selectedItems[0].kbId);
    return firstKbId;
  }
  const firstKb = kbList[0];
  return firstKb ? String(firstKb.kbId) : '';
};

/**
 * 将 NewRegionSelect 的选区值构建为 ISelectedKnowledgeTagData[] 标签数据
 * @param value - NewRegionSelect 选区值
 * @param kbList - 知识库列表（含文档详情）
 * @returns 标签数据列表
 */
export const buildKnowledgeTagData = (
  value: RegionSelectValueType,
  kbList: IKnowledgeBaseItem[],
): ISelectedKnowledgeTagData[] => {
  const result: ISelectedKnowledgeTagData[] = [];
  const kbMap = new Map(kbList.map((kb) => [kb.kbId, kb]));

  (value.regions || []).forEach((kbId) => {
    const kb = kbMap.get(kbId);
    if (!kb) return;
    const hasDoc = (value.markets || []).some((market) =>
      market.startsWith(`${kbId}__`),
    );
    if (hasDoc) return;
    result.push({
      kbId,
      kbName: kb.kbName,
      tenantId: kb.tenantId,
    });
  });

  (value.markets || []).forEach((market) => {
    const [kbId, docId] = market.split('__');
    if (!kbId || !docId || docId === 'empty') return;
    const kb = kbMap.get(kbId);
    if (!kb) return;
    const doc = (kb.docs || []).find((item) => item.docId === docId);
    if (!doc?.docName) return;
    result.push({
      kbId,
      kbName: kb.kbName,
      tenantId: resolveTenantId(doc.tenantId, kb.tenantId),
      docId,
      docName: doc.docName,
      fileName: doc.fileName,
      fileType: doc.fileType,
    });
  });

  return result;
};

const isSelectValueEmpty = (value: RegionSelectValueType): boolean => {
  const markets = value?.markets || [];
  const regions = value?.regions || [];
  return markets.length === 0 && regions.length === 0;
};

/**
 * 关面板提交：优先用 kbList 解析；kbList 暂不可用时从 previousItems 按选区兜底
 */
/** 比较两组知识库 tag 是否相同（按 id 集合，忽略顺序） */
export const areKnowledgeTagsEqual = (
  a: ISelectedKnowledgeTagData[],
  b: ISelectedKnowledgeTagData[],
): boolean => {
  if (a.length !== b.length) return false;
  const toKey = (items: ISelectedKnowledgeTagData[]) =>
    items
      .map((item) => getKnowledgeTagKey(item))
      .sort()
      .join('|');
  return toKey(a) === toKey(b);
};

export const resolveCommitKnowledgeTags = (
  value: RegionSelectValueType,
  kbList: IKnowledgeBaseItem[],
  previousItems: ISelectedKnowledgeTagData[],
): ISelectedKnowledgeTagData[] => {
  if (isSelectValueEmpty(value)) {
    return [];
  }

  const kbMap = new Map(kbList.map((kb) => [kb.kbId, kb]));
  const prevByKey = new Map(
    previousItems.map((item) => [getKnowledgeTagKey(item), item]),
  );
  const merged = new Map<string, ISelectedKnowledgeTagData>();

  buildKnowledgeTagData(value, kbList)
    .filter((item) => Boolean(item.kbName))
    .forEach((item) => {
      merged.set(getKnowledgeTagKey(item), item);
    });

  (value.markets || []).forEach((market) => {
    const [kbId, docId] = market.split('__');
    if (!kbId || !docId || docId === 'empty') return;

    const key = getKnowledgeTagKey({ kbId, docId });
    if (merged.has(key)) return;

    const kb = kbMap.get(kbId);
    const doc = kb?.docs?.find((item) => item.docId === docId);
    const prev = prevByKey.get(key);
    const tag: ISelectedKnowledgeTagData = {
      kbId,
      kbName: kb?.kbName || prev?.kbName || '',
      tenantId: resolveTenantId(doc?.tenantId, kb?.tenantId, prev?.tenantId),
      docId,
      docName: doc?.docName || prev?.docName || '',
      fileName: doc?.fileName || prev?.fileName,
      fileType: doc?.fileType || prev?.fileType,
    };
    if (tag.kbName && tag.docId) {
      merged.set(key, tag);
    }
  });

  (value.regions || []).forEach((kbId) => {
    const hasDoc = (value.markets || []).some((market) =>
      market.startsWith(`${kbId}__`),
    );
    if (hasDoc) return;

    const key = getKnowledgeTagKey({ kbId });
    if (merged.has(key)) return;

    const kb = kbMap.get(kbId);
    const prev = previousItems.find((item) => item.kbId === kbId && !item.docId);
    const tag: ISelectedKnowledgeTagData = {
      kbId,
      kbName: kb?.kbName || prev?.kbName || '',
      tenantId: resolveTenantId(kb?.tenantId, prev?.tenantId),
    };
    if (tag.kbName) {
      merged.set(key, tag);
    }
  });

  if (merged.size > 0) {
    return collapseSelectedKnowledgeToWholeKb(
      Array.from(merged.values()),
      kbList,
      previousItems,
    );
  }

  const marketSet = new Set(value.markets || []);
  const regionSet = new Set(value.regions || []);
  return collapseSelectedKnowledgeToWholeKb(
    previousItems.filter((item) => {
      if (item.docId) {
        return marketSet.has(`${item.kbId}__${item.docId}`);
      }
      return regionSet.has(item.kbId);
    }),
    kbList,
    previousItems,
  );
};

/**
 * 左侧全选：当前列表每个库整库引用，不依赖文档是否已加载。
 * @param kbList - 当前面板列表（含搜索过滤后）
 * @returns NewRegionSelect 选区（regions 全库、markets 空）
 */
export const buildWholeKbSelectAllRegionsValue = (
  kbList: IKnowledgeBaseItem[],
): RegionSelectValueType => ({
  type: VALUE_TYPE_REGION,
  regions: kbList.map((kb) => String(kb.kbId)),
  markets: [],
});

/** 文档未加载占位 market 后缀 */
export const KB_EMPTY_MARKET_SUFFIX = '__empty';

/**
 * 是否为文档未加载占位 market
 * @param market - market 值
 */
export const isKbEmptyPlaceholderMarket = (market: string): boolean =>
  market.endsWith(KB_EMPTY_MARKET_SUFFIX);

/**
 * 选区 markets/regions 是否变化（用于 NR remount）
 * @param prev - 旧选区
 * @param next - 新选区
 */
export const isRegionSelectValueChanged = (
  prev: RegionSelectValueType,
  next: RegionSelectValueType,
): boolean => {
  const serialize = (value: RegionSelectValueType) => ({
    markets: [...(value.markets || [])].sort().join('|'),
    regions: [...(value.regions || [])].sort().join('|'),
  });
  const a = serialize(prev);
  const b = serialize(next);
  return a.markets !== b.markets || a.regions !== b.regions;
};

/**
 * 当前库是否为整库 regions（无该库文档 markets）
 * @param value - 选区
 * @param regionId - 库 id
 */
export const isWholeRegionKbSelected = (
  value: RegionSelectValueType | null,
  regionId: string,
): boolean => {
  if (!regionId) {
    return false;
  }
  const { regions = [], markets = [] } = value || {};
  return regions.includes(regionId)
    && !markets.some((market) => market.startsWith(`${regionId}__`));
};

/**
 * 库在 UI 上是否视为「全选」
 * @param value - 选区
 * @param regionId - 库 id
 * @param selectableChildValues - 可勾选文档 value 列表
 */
export const isRegionKbFullySelected = (
  value: RegionSelectValueType | null,
  regionId: string,
  selectableChildValues: string[],
): boolean => {
  if (!regionId) {
    return false;
  }
  if (isWholeRegionKbSelected(value, regionId)) {
    return true;
  }
  if (!selectableChildValues.length) {
    return (value?.regions ?? []).includes(regionId);
  }
  const markets = value?.markets ?? [];
  return selectableChildValues.every((childValue) => markets.includes(childValue));
};

/**
 * 切换单库整库 regions
 * @param prev - 当前选区
 * @param regionId - 库 id
 * @param selectWhole - 是否整库选中
 */
export const applyWholeRegionKbSelection = (
  prev: RegionSelectValueType,
  regionId: string,
  selectWhole: boolean,
): RegionSelectValueType => {
  const id = String(regionId);
  if (!id) {
    return prev;
  }
  const { regions = [], markets = [] } = prev;
  const filteredMarkets = markets.filter(
    (market) => !market.startsWith(`${id}__`) && !isKbEmptyPlaceholderMarket(market),
  );
  if (selectWhole) {
    return {
      type: VALUE_TYPE_REGION,
      regions: uniq([...regions.filter((r) => r !== id), id]),
      markets: filteredMarkets,
    };
  }
  return {
    type: VALUE_TYPE_REGION,
    regions: regions.filter((r) => r !== id),
    markets: filteredMarkets,
  };
};

/**
 * 文档 markets 变更：保留其它库整库 regions
 * @param prev - 当前选区
 * @param nextMarkets - NR 返回的 markets
 */
export const applyRegionModeMarketValue = (
  prev: RegionSelectValueType,
  nextMarkets: string[],
): RegionSelectValueType => {
  const { regions: prevRegions = [] } = prev;
  const uniqueMarkets = uniq(
    nextMarkets.filter((market) => !isKbEmptyPlaceholderMarket(market)),
  );
  const nextRegions = prevRegions.filter((regionId) => {
    return !uniqueMarkets.some((market) => market.startsWith(`${regionId}__`));
  });
  return {
    type: VALUE_TYPE_REGION,
    regions: nextRegions,
    markets: uniqueMarkets,
  };
};

/**
 * 取 option 下可勾选文档 value
 * @param option - 左栏库 option
 */
export const getSelectableChildValues = (
  option: OptionsItemType | undefined,
): string[] => (option?.children ?? [])
  .filter(
    (child) => !child.disabled
      && !child.isGroupHeader
      && !isKbEmptyPlaceholderMarket(child.value),
  )
  .map((child) => child.value);

/**
 * 归一化 NR onChange：按库逐块重建选区
 * @param prev - 业务层 canonical 选区
 * @param nrNext - NR 回调值
 * @param options - 当前 options
 */
export const normalizeRegionModePanelValue = (
  prev: RegionSelectValueType,
  nrNext: RegionSelectValueType,
  options: OptionsItemType[],
): RegionSelectValueType => {
  const rawMarkets = nrNext.markets ?? [];
  const visibleKbIds = new Set(options.map((item) => String(item.value)));

  const preservedRegions = (prev.regions ?? []).filter((id) => !visibleKbIds.has(id));
  const preservedMarkets = (prev.markets ?? []).filter((market) => {
    const kbId = market.split('__')[0];
    return !visibleKbIds.has(kbId);
  });

  let result: RegionSelectValueType = {
    type: VALUE_TYPE_REGION,
    regions: preservedRegions,
    markets: preservedMarkets,
  };

  options.forEach((option) => {
    const kbId = String(option.value);
    const emptyKey = `${kbId}${KB_EMPTY_MARKET_SUFFIX}`;
    const childValues = getSelectableChildValues(option);
    const kbRawMarkets = rawMarkets.filter(
      (market) => market.startsWith(`${kbId}__`)
        && !isKbEmptyPlaceholderMarket(market),
    );

    if (rawMarkets.includes(emptyKey)) {
      result = applyWholeRegionKbSelection(result, kbId, true);
      return;
    }

    if (!childValues.length) {
      if (isWholeRegionKbSelected(prev, kbId)) {
        result = applyWholeRegionKbSelection(result, kbId, true);
      }
      return;
    }

    const allSelected = childValues.every((value) => kbRawMarkets.includes(value));
    const anySelected = kbRawMarkets.length > 0;

    if (allSelected) {
      result = applyWholeRegionKbSelection(result, kbId, true);
      return;
    }
    if (!anySelected) {
      result = applyWholeRegionKbSelection(result, kbId, false);
      return;
    }

    result = applyWholeRegionKbSelection(result, kbId, false);
    const otherMarkets = (result.markets ?? []).filter(
      (market) => !market.startsWith(`${kbId}__`),
    );
    result = {
      type: VALUE_TYPE_REGION,
      regions: result.regions ?? [],
      markets: uniq([...otherMarkets, ...kbRawMarkets]),
    };
  });

  if (!(result.regions ?? []).length && !(result.markets ?? []).length) {
    return KB_EMPTY_VALUE;
  }
  return result;
};

/**
 * 展示层：整库 regions 展开为 NR Checkbox 需要的 markets
 * @param value - canonical 选区
 * @param options - 当前 options
 */
export const expandRegionModeDisplayValue = (
  value: RegionSelectValueType,
  options: OptionsItemType[],
): RegionSelectValueType => {
  const regions = value?.regions ?? [];
  if (!regions.length) {
    return value;
  }
  const expandedMarkets = new Set(
    (value?.markets ?? []).filter((market) => !isKbEmptyPlaceholderMarket(market)),
  );
  regions.forEach((regionId) => {
    const option = options.find((item) => item.value === regionId);
    const children = option?.children ?? [];
    const realChildren = children.filter(
      (child) => !isKbEmptyPlaceholderMarket(child.value),
    );
    if (realChildren.length > 0) {
      realChildren.forEach((child) => {
        if (child.disabled || child.isGroupHeader) {
          return;
        }
        expandedMarkets.add(child.value);
      });
      return;
    }
    if (children.some((child) => isKbEmptyPlaceholderMarket(child.value))) {
      expandedMarkets.add(`${regionId}${KB_EMPTY_MARKET_SUFFIX}`);
    }
  });
  return {
    ...value,
    type: VALUE_TYPE_REGION,
    regions,
    markets: Array.from(expandedMarkets),
  };
};

/** 右栏全选当前库 → 整库 regions */
export const buildSingleKbWholeRegionValue = (
  prev: RegionSelectValueType,
  kbId: string,
): RegionSelectValueType => applyWholeRegionKbSelection(prev, kbId, true);

/** 右栏清空当前库 */
export const buildSingleKbClearRegionValue = (
  prev: RegionSelectValueType,
  kbId: string,
): RegionSelectValueType => {
  const result = applyWholeRegionKbSelection(prev, kbId, false);
  if (!(result.regions ?? []).length && !(result.markets ?? []).length) {
    return KB_EMPTY_VALUE;
  }
  return result;
};

/** 左栏全选可见库 */
export const mergeWholeKbRegionsForKbList = (
  prev: RegionSelectValueType,
  kbList: IKnowledgeBaseItem[],
): RegionSelectValueType => {
  let result = prev;
  kbList.forEach((kb) => {
    result = applyWholeRegionKbSelection(result, String(kb.kbId), true);
  });
  return result;
};

/** 左栏清空可见库 */
export const clearWholeKbRegionsForKbList = (
  prev: RegionSelectValueType,
  kbList: IKnowledgeBaseItem[],
): RegionSelectValueType => {
  const visibleIds = new Set(kbList.map((kb) => String(kb.kbId)));
  const regions = (prev.regions ?? []).filter((id) => !visibleIds.has(id));
  const markets = (prev.markets ?? []).filter((market) => {
    const kbId = market.split('__')[0];
    return !visibleIds.has(kbId);
  });
  if (!regions.length && !markets.length) {
    return KB_EMPTY_VALUE;
  }
  return {
    type: regions.length ? VALUE_TYPE_REGION : VALUE_TYPE_MARKET,
    regions,
    markets,
  };
};

/** 面板重开需预拉文档的 kbId */
export const resolveKbIdsNeedingDocsForPanelRestore = (
  selectedItems: ISelectedKnowledgeTagData[],
): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  selectedItems.forEach((item) => {
    if (isWholeKbTag(item)) {
      return;
    }
    const kbId = String(item.kbId);
    if (seen.has(kbId)) {
      return;
    }
    seen.add(kbId);
    ids.push(kbId);
  });
  return ids;
};

/** 仅 docName 待解析的 tag 需等文档 */
export const resolveKbIdsNeedingDocsForDocNameRestore = (
  selectedItems: ISelectedKnowledgeTagData[],
): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  selectedItems.forEach((item) => {
    if (isWholeKbTag(item) || item.docId || !item.docName) {
      return;
    }
    const kbId = String(item.kbId);
    if (seen.has(kbId)) {
      return;
    }
    seen.add(kbId);
    ids.push(kbId);
  });
  return ids;
};

/** docName 解析所需文档是否均已加载 */
export const isPanelRestoreDocsReady = (
  selectedItems: ISelectedKnowledgeTagData[],
  docsByKbId: Record<string, unknown[]>,
): boolean => {
  const kbIds = resolveKbIdsNeedingDocsForDocNameRestore(selectedItems);
  if (!kbIds.length) {
    return true;
  }
  return kbIds.every((kbId) => docsByKbId[kbId] !== undefined);
};
